(function () {
  'use strict';

  const marker = '_mirai_threshold_gift';
  const isGift = (item) => item.properties?.[marker] === 'v1';

  function plan(cart, config) {
    const gifts = cart.items.filter(isGift);
    const spend = Math.max(0, cart.total_price - gifts.reduce((sum, item) => sum + item.final_line_price, 0));
    const eligible = config.enabled && config.available && config.variantId &&
      Number.isFinite(config.threshold) && config.threshold > 0 && spend >= config.threshold;
    const updates = {};
    let keeper = null;
    for (const gift of gifts) {
      if (eligible && !keeper && String(gift.variant_id) === String(config.variantId) && !gift.selling_plan_allocation) {
        keeper = gift;
        if (gift.quantity !== 1) updates[gift.key] = 1;
      } else {
        updates[gift.key] = 0;
      }
    }
    return { updates, add: Boolean(eligible && !keeper), spend };
  }

  if (typeof module === 'object' && module.exports) {
    module.exports = { plan, isGift };
    return;
  }

  const node = document.getElementById('mirai-threshold-gift-config');
  if (!node || window.MiraiThresholdGift) return;
  const config = JSON.parse(node.textContent);
  if (!config.enabled && !config.hasGift) return;

  const root = config.root;
  const baseFetch = window.fetch.bind(window);
  const themeEvents = import('@shopify/events');
  const ownEvents = new WeakSet();
  const externalRequests = new Set();
  let revision = 0;
  let dirty = false;
  let refreshRequested = false;
  let running = null;
  let timer;
  let bypassCheckout = false;
  let checkoutPending = false;
  let failedAddSignature = '';
  let error = null;
  let opusCartSignature = '';
  const opusSignature = (cart) => JSON.stringify([cart.total_price, cart.items?.map((item) => [item.key, item.quantity, item.final_line_price])]);
  let latestCart;
  let observedOpus;
  function hideGiftQuantity() {
    const root = document.getElementById('opus-drawer-id');
    if (!root || !latestCart) return;
    for (const control of root.querySelectorAll('[data-mirai-gift-quantity]')) control.removeAttribute('data-mirai-gift-quantity');
    const groups = new Map();
    for (const item of latestCart.items) {
      const id = String(item.variant_id);
      if (!groups.has(id)) groups.set(id, []);
      groups.get(id).push(item);
    }
    for (const [id, items] of groups) {
      if (!/^\d+$/.test(id)) continue;
      const rows = root.querySelectorAll('.cd-product-item-content.id' + id);
      if (rows.length !== items.length) continue;
      rows.forEach((row, index) => {
        if (isGift(items[index])) row.querySelector('.cd-number-input')?.setAttribute('data-mirai-gift-quantity', '');
      });
    }
    if (typeof MutationObserver !== 'undefined' && observedOpus !== root) {
      observedOpus = root;
      new MutationObserver(hideGiftQuantity).observe(root, { childList: true, subtree: true });
    }
    if (!document.getElementById('mirai-gift-quantity-style')) {
      const style = document.createElement('style');
      style.id = 'mirai-gift-quantity-style';
      style.textContent = '[data-mirai-gift-quantity] { display: none !important; }';
      document.head.appendChild(style);
    }
  }
  function syncOpus(cart) {
    latestCart = cart;
    hideGiftQuantity();
    const next = opusSignature(cart);
    if (typeof window.OpusSetCartData === 'function' && next !== opusCartSignature) {
      opusCartSignature = next;
      window.OpusSetCartData(cart);
    }
  }

  function isCartMutation(input) {
    try {
      const url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, location.origin);
      return url.origin === location.origin && /\/cart\/(add|change|update|clear)(\.js)?\/?$/.test(url.pathname);
    } catch (_) {
      return false;
    }
  }

  async function request(path, body) {
    const response = await baseFetch(root + path, {
      method: body ? 'POST' : 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: path.includes('?sections=') ? { Accept: 'text/html' } : { 'Content-Type': 'application/json', Accept: 'application/json' },
      ...(body ? { body: JSON.stringify(body) } : {}),
      signal: AbortSignal.timeout(10000),
    });
    if (!response.ok) throw new Error('Gift cart request failed: ' + response.status);
    return response.json();
  }

  function activeConfig(cart) {
    let rate = 1;
    if (cart.currency !== config.currency) {
      const currency = window.Shopify?.currency;
      rate = currency?.active === cart.currency ? Number(currency.rate) : NaN;
    }
    return { ...config, threshold: Number.isFinite(rate) && rate > 0 ? Math.round(config.threshold * rate) : NaN };
  }

  function signature(cart) {
    return JSON.stringify({
      currency: cart.currency,
      items: cart.items.filter((item) => !isGift(item)).map((item) => [item.key, item.quantity, item.final_line_price]),
      discounts: cart.discount_codes,
    });
  }

  function sectionTargets() {
    const targets = [
      { section: 'cart-drawer', selector: '#CartDrawer' },
      { section: 'cart-icon-bubble', selector: '#cart-icon-bubble', source: '.shopify-section' },
    ];
    for (const id of ['main-cart-items', 'main-cart-footer']) {
      const element = document.getElementById(id);
      if (element?.dataset.id) targets.push({ section: element.dataset.id, selector: '#' + id });
    }
    return targets;
  }

  async function refresh(cart, version) {
    const nativeCarts = [...document.querySelectorAll('cart-items-component')];
    if (nativeCarts.length) {
      const ids = [...new Set(nativeCarts.map((element) => element.dataset.sectionId).filter(Boolean))];
      const sections = await request('cart?sections=' + ids.map(encodeURIComponent).join(','));
      const { CartLinesUpdateEvent } = await themeEvents;
      if (version !== revision || externalRequests.size) {
        dirty = true;
        refreshRequested = true;
        return;
      }
      const event = new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: [],
        promise: Promise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { sections, items: cart.items, itemCount: cart.item_count, source: 'mirai-free-gift' },
        }),
      });
      ownEvents.add(event);
      document.dispatchEvent(event);
      return;
    }
    const targets = sectionTargets();
    const sections = await request('cart?sections=' + targets.map((target) => encodeURIComponent(target.section)).join(','));
    if (version !== revision || externalRequests.size) {
      dirty = true;
      refreshRequested = true;
      return;
    }
    syncOpus(cart);
    const activeId = document.activeElement?.id;
    const parser = new DOMParser();
    for (const target of targets) {
      if (typeof sections[target.section] !== 'string') continue;
      const source = parser.parseFromString(sections[target.section], 'text/html').querySelector(target.source || target.selector);
      const destination = document.querySelector(target.selector);
      if (source && destination) destination.innerHTML = source.innerHTML;
    }
    document.querySelectorAll('cart-drawer, cart-drawer-items, cart-items, #main-cart-footer').forEach((element) => {
      element.classList.toggle('is-empty', cart.item_count === 0);
    });
    const drawer = document.querySelector('cart-drawer');
    drawer?.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close());
    if (activeId) document.getElementById(activeId)?.focus({ preventScroll: true });
    document.dispatchEvent(new CustomEvent('mirai:gift-updated', { detail: { cart } }));
  }

  async function reconcile() {
    let changed = false;
    let cart;
    for (let attempt = 0; attempt < 6; attempt++) {
      await Promise.allSettled([...externalRequests]);
      const version = revision;
      cart = await request('cart.js');
      if (version !== revision || externalRequests.size) continue;
      const action = plan(cart, activeConfig(cart));
      if (Object.keys(action.updates).length) {
        await request('cart/update.js', { updates: action.updates });
        changed = true;
        continue;
      }
      if (action.add && failedAddSignature !== signature(cart)) {
        const before = signature(cart);
        try {
          await request('cart/add.js', { items: [{ id: config.variantId, quantity: 1, properties: { [marker]: 'v1' } }] });
        } catch (failure) {
          failedAddSignature = before;
          console.warn('[Free gift] Gift unavailable; keeping the existing cart.', failure);
        }
        changed = true;
        const after = await request('cart.js');
        if (version !== revision || externalRequests.size) continue;
        if (!plan(after, activeConfig(after)).add && plan(after, activeConfig(after)).spend < activeConfig(after).threshold) {
          failedAddSignature = before;
        }
        continue;
      }
      syncOpus(cart);
      if (changed || refreshRequested) {
        refreshRequested = false;
        await refresh(cart, version);
      }
      return cart;
    }
    throw new Error('Cart changed repeatedly while checking the free gift.');
  }

  async function drain() {
    do {
      dirty = false;
      error = null;
      try {
        if (navigator.locks) {
          await navigator.locks.request('mirai-threshold-gift', reconcile);
        } else {
          await reconcile();
        }
      } catch (failure) {
        error = failure;
        dirty = false;
        console.warn('[Free gift] Cart synchronization failed.', failure);
      }
    } while (dirty);
  }

  function sync() {
    refreshRequested = true;
    clearTimeout(timer);
    dirty = true;
    if (!running) {
      running = drain().finally(() => { running = null; });
    }
    return running;
  }

  function schedule() {
    refreshRequested = true;
    dirty = true;
    clearTimeout(timer);
    timer = setTimeout(sync, 200);
  }

  window.fetch = function (input, options) {
    if (!isCartMutation(input)) return baseFetch(input, options);
    revision++;
    const pending = baseFetch(input, options);
    externalRequests.add(pending);
    const finished = () => {
      externalRequests.delete(pending);
      revision++;
      schedule();
    };
    pending.then(finished, finished);
    return pending;
  };

  const open = XMLHttpRequest.prototype.open;
  const send = XMLHttpRequest.prototype.send;
  const watched = new WeakSet();
  XMLHttpRequest.prototype.open = function (method, url, ...args) {
    watched.delete(this);
    if (isCartMutation(url)) watched.add(this);
    return open.call(this, method, url, ...args);
  };
  XMLHttpRequest.prototype.send = function (...args) {
    if (!watched.has(this)) return send.apply(this, args);
    revision++;
    let resolve;
    const pending = new Promise((done) => { resolve = done; });
    externalRequests.add(pending);
    const finished = () => {
      externalRequests.delete(pending);
      revision++;
      resolve();
      schedule();
    };
    this.addEventListener('loadend', finished, { once: true });
    try { return send.apply(this, args); } catch (failure) { finished(); throw failure; }
  };

  function showError() {
    const target = document.getElementById('CartDrawer-CartErrors') || document.getElementById('cart-errors');
    if (target) target.textContent = window.cartStrings?.error || config.error;
    else window.alert(config.error);
  }

  async function beforeCheckout(event, resume) {
    if (bypassCheckout) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkoutPending) return;
    checkoutPending = true;
    await sync();
    checkoutPending = false;
    if (error) { showError(); return; }
    bypassCheckout = true;
    try { resume(); } finally { bypassCheckout = false; }
  }

  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('a[href]');
    if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const url = new URL(link.href, location.origin);
    if (url.origin === location.origin && /\/checkout\/?$/.test(url.pathname)) {
      beforeCheckout(event, () => link.click());
    }
  }, true);
  document.addEventListener('submit', (event) => {
    if (event.submitter?.name !== 'checkout') return;
    const form = event.target;
    const buttonId = event.submitter.id;
    beforeCheckout(event, () => {
      const button = document.getElementById(buttonId) || form.querySelector('[name="checkout"]');
      const currentForm = button?.form || (form.id && document.getElementById(form.id)) || form;
      currentForm.requestSubmit(button || undefined);
    });
  }, true);

  window.addEventListener('OpusCartChange', (event) => {
    if (event.detail?.items) opusCartSignature = opusSignature(event.detail);
    schedule();
  });
  window.addEventListener('OpusOpenCartState', (event) => { if (event.detail?.isOpen) schedule(); });
  window.addEventListener('pageshow', schedule);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) schedule(); });
  for (const name of ['cart:updated', 'cart:refresh', 'liquid-ajax-cart:request-end']) {
    document.addEventListener(name, schedule);
  }
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, schedule);
  }
  themeEvents.then(({ StandardEvents }) => {
    for (const name of [StandardEvents.cartLinesUpdate, StandardEvents.cartDiscountUpdate]) {
      document.addEventListener(name, (event) => {
        if (ownEvents.has(event)) return;
        Promise.resolve(event.promise).then(schedule, schedule);
      });
    }
  }).catch((failure) => console.warn('[Free gift] Theme events unavailable.', failure));
  window.MiraiThresholdGift = { sync };
  schedule();
})();
